import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, FolderOpen, X } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import MediaPicker from "@/components/admin/MediaPicker";
import { ConfirmModal } from "@/pages/admin/AdminPortfolio";

const EMPTY = { name: "", position: "", photo: "", text: "", show_on_home: true, project_slug: "" };

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, ...form}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/admin/portfolio").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/testimonials")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load testimonials"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data[0]) setEditing((e) => ({ ...e, photo: data[0].url }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.text.trim()) {
      toast.error("Name and testimonial text are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: editing.name,
      position: editing.position,
      photo: editing.photo,
      text: editing.text,
      show_on_home: editing.show_on_home !== false,
      project_slug: editing.project_slug || "",
    };
    try {
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
      } else {
        await api.post("/admin/testimonials", payload);
      }
      toast.success("Testimonial saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/testimonials/${deleteTarget.id}`);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Testimonial deleted");
    } catch {
      toast.error("Could not delete testimonial");
    }
    setDeleteTarget(null);
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-testimonials-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Testimonials</h1>
          <p className="mt-1 text-sm text-neutral-500">Shown in the Testimonials section on your homepage</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary !px-5 !py-2.5 text-xs" data-testid="new-testimonial-button">
            <Plus className="h-4 w-4" /> New Testimonial
          </button>
        )}
      </div>

      {editing && (
        <section className="glass mb-8 rounded-3xl p-6 md:p-8" data-testid="testimonial-form">
          <div className="mb-6 flex items-center justify-between">
            <p className="eyebrow">{editing.id ? "Edit Testimonial" : "New Testimonial"}</p>
            <button onClick={() => setEditing(null)} aria-label="Close form" data-testid="testimonial-cancel-button" className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="label-admin" htmlFor="tf-name">Name</label>
              <input id="tf-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-glass" placeholder="Claire Aurele" data-testid="testimonial-name-input" />
            </div>
            <div>
              <label className="label-admin" htmlFor="tf-position">Position</label>
              <input id="tf-position" value={editing.position} onChange={(e) => setEditing({ ...editing, position: e.target.value })} className="input-glass" placeholder="Founder, Maison AURELE" data-testid="testimonial-position-input" />
            </div>
            <div className="md:col-span-2">
              <label className="label-admin">Photo</label>
              {editing.photo ? (
                <div className="flex items-center gap-4">
                  <img src={mediaUrl(editing.photo)} alt="Client" className="h-16 w-16 rounded-xl border border-white/10 object-cover" data-testid="testimonial-photo-preview" />
                  <button onClick={() => setEditing({ ...editing, photo: "" })} className="btn-ghost !py-2 !px-4 text-xs" data-testid="testimonial-photo-remove">
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <label className="btn-ghost !py-2.5 text-xs cursor-pointer" data-testid="testimonial-photo-upload">
                    <Upload className="h-4 w-4" /> Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} />
                  </label>
                  <button type="button" onClick={() => setPickerOpen(true)} className="btn-ghost !py-2.5 text-xs" data-testid="testimonial-photo-library">
                    <FolderOpen className="h-4 w-4" /> From library
                  </button>
                </div>
              )}
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-6">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={editing.show_on_home !== false}
                  onChange={(e) => setEditing({ ...editing, show_on_home: e.target.checked })}
                  className="h-4 w-4 accent-white"
                  data-testid="testimonial-show-home"
                />
                <span className="text-sm text-neutral-300">Display on homepage</span>
              </label>
              <label className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-neutral-300">Related project (shows on its page):</span>
                <select
                  value={editing.project_slug || ""}
                  onChange={(e) => setEditing({ ...editing, project_slug: e.target.value })}
                  className="input-glass !w-auto !py-2 text-xs"
                  data-testid="testimonial-project-select"
                >
                  <option value="">None</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.slug}>{p.title}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="label-admin" htmlFor="tf-text">Testimonial</label>
              <textarea id="tf-text" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={4} className="input-glass resize-none" placeholder="What the client said about working with you…" data-testid="testimonial-text-input" />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="testimonial-save-button">
              {saving ? "Saving…" : "Save Testimonial"}
            </button>
            <button onClick={() => setEditing(null)} className="btn-ghost !py-2.5 text-xs">
              Cancel
            </button>
          </div>
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className="h-40 animate-pulse rounded-3xl bg-white/[0.04]" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="glass rounded-3xl p-10 text-center text-sm text-neutral-500" data-testid="testimonials-empty">
          No testimonials yet — add your first one.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="testimonials-list">
          {items.map((t) => (
            <figure key={t.id} className="glass rounded-3xl p-6" data-testid={`testimonial-card-${t.id}`}>
              <blockquote className="line-clamp-4 text-sm leading-relaxed text-neutral-300">“{t.text}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-white/[0.08] pt-4">
                {t.photo ? (
                  <img src={mediaUrl(t.photo)} alt={t.name} className="h-11 w-11 rounded-xl border border-white/10 object-cover" />
                ) : (
                  <span className="glass flex h-11 w-11 items-center justify-center rounded-xl font-display text-sm font-semibold text-white">
                    {(t.name || "?")[0]}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-white">{t.name}</p>
                  <p className="truncate text-xs text-neutral-500">{t.position}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...t })} aria-label={`Edit ${t.name}`} data-testid={`edit-testimonial-${t.id}`} className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-white">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setDeleteTarget({ id: t.id, title: t.name })} aria-label={`Delete ${t.name}`} data-testid={`delete-testimonial-${t.id}`} className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-red-300">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(m) => { setEditing((e) => ({ ...e, photo: m.url })); setPickerOpen(false); }} />
      <ConfirmModal target={deleteTarget} noun="testimonial" onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
    </div>
  );
}
